import React, { useEffect, useRef, useState } from 'react';
import LectureReminderNotice from './LectureReminderNotice';
import useDraggablePanel from '../../hooks/useDraggablePanel';
import { getFullSessionLectureWallpaperUrl } from '../../assets/assetPaths';
import {
  hasSeenLecture,
  markLectureSeen,
  subscribeToLectureViewingState,
} from '../../services/lectureViewingState';

const FULL_SESSION_LECTURE_ID = 'full-session';

function ClientWheelSelector({
  theme = 'dark',
  t,
  showClientGuidedHighlight,
  currentClientNumber,
  minProfileId,
  maxProfileId,
  handleWheelScroll,
  handleWheelKeyDown,
  selectAdjacentClient,
  WheelArrowUp,
  WheelArrowDown,
  handleRandomClient,
  isRandomizing,
  DiceIcon,
  onCustomClient,
  ClipboardPencilIcon,
  hideCustomClientOption = false,
  suppressLectureReminder = false,
}) {
  const [isLectureVideoOpen, setIsLectureVideoOpen] = useState(false);
  const lectureDrag = useDraggablePanel(isLectureVideoOpen);
  const [lectureReminderOpen, setLectureReminderOpen] = useState(false);
  const [lectureViewingRevision, setLectureViewingRevision] = useState(0);
  const lectureVideoRef = useRef(null);

  useEffect(() => subscribeToLectureViewingState(() => {
    setLectureViewingRevision((revision) => revision + 1);
  }), []);

  useEffect(() => {
    if (
      !currentClientNumber ||
      suppressLectureReminder ||
      hasSeenLecture(FULL_SESSION_LECTURE_ID)
    ) {
      setLectureReminderOpen(false);
      return;
    }

    setLectureReminderOpen(true);
  }, [
    currentClientNumber,
    lectureViewingRevision,
    suppressLectureReminder,
  ]);

  const handleOpenLectureVideo = () => {
    markLectureSeen(FULL_SESSION_LECTURE_ID);
    setLectureReminderOpen(false);
    setIsLectureVideoOpen(true);
  };

  return (
    <>
      <div className="client-selector client-selector--with-lecture">
        <label className="selector-label">{t('sidebar_select_label')}</label>

        <div
          className={`client-wheel-picker${showClientGuidedHighlight ? ' client-selection-cue' : ''}`}
        >
          <div className="wheel-inline-row">
            <span className="wheel-inline-label">{t('sidebar_wheel_label')}</span>
            <div className="wheel-horizontal-control" role="group" aria-label={t('sidebar_wheel_label')}>
              <div
                className="wheel-window"
                tabIndex={0}
                role="spinbutton"
                aria-valuenow={currentClientNumber ?? undefined}
                aria-valuetext={
                  currentClientNumber ? t('sidebar_wheel_value', { number: currentClientNumber }) : t('sidebar_wheel_none')
                }
                aria-valuemin={minProfileId ?? undefined}
                aria-valuemax={maxProfileId ?? undefined}
                onWheel={handleWheelScroll}
                onKeyDown={handleWheelKeyDown}
                data-guided-highlight-purpose={showClientGuidedHighlight ? 'Choose which client you want to practise with.' : undefined}
              >
                <span className="wheel-window-number">
                  {currentClientNumber ?? '--'}
                </span>
                <div className="wheel-scroll-track">
                  <button
                    type="button"
                    className="wheel-scroll-btn"
                    aria-label={t('sidebar_next_client')}
                    onClick={() => selectAdjacentClient(1)}
                  >
                    <WheelArrowUp />
                  </button>
                  <button
                    type="button"
                    className="wheel-scroll-btn"
                    aria-label={t('sidebar_prev_client')}
                    onClick={() => selectAdjacentClient(-1)}
                  >
                    <WheelArrowDown />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="random-client-row">
            <span className="wheel-inline-label">{t('sidebar_random_label')}</span>
            <button
              className="random-client-btn"
              onClick={handleRandomClient}
              disabled={isRandomizing}
              aria-label={isRandomizing ? t('sidebar_random_selecting') : t('sidebar_random_select')}
              title={isRandomizing ? t('sidebar_random_selecting') : t('sidebar_random_select')}
              data-guided-highlight-purpose={showClientGuidedHighlight ? 'Select a client at random and begin practising.' : undefined}
            >
              {isRandomizing ? (
                <span className="random-client-spinner" aria-hidden="true" />
              ) : (
                <DiceIcon />
              )}
            </button>
          </div>

          {!hideCustomClientOption && (
            <div className="custom-client-row">
              <span className="wheel-inline-label">{t('sidebar_custom_label')}</span>
              <button
                className="custom-client-btn"
                onClick={() => onCustomClient && onCustomClient()}
                aria-label={t('sidebar_custom_title')}
                title={t('sidebar_custom_title')}
              >
                <ClipboardPencilIcon />
              </button>
            </div>
          )}
        </div>

        <div className="sidebar-lecture-video">
          <p className="selector-description sidebar-lecture-video-label">Accompanying Lecture Video:</p>
          <button
            type="button"
            ref={lectureVideoRef}
            className="sidebar-lecture-video-frame"
            onClick={handleOpenLectureVideo}
            aria-label="Open Full Session lecture video"
            title="Open Full Session lecture video"
          >
            <img
              src={getFullSessionLectureWallpaperUrl(theme)}
              alt="Full Session lecture video thumbnail"
            />
          </button>
        </div>
      </div>
      {lectureReminderOpen && (
        <LectureReminderNotice
          anchorRef={lectureVideoRef}
          t={t}
          theme={theme}
          title={t('full_session_lecture_reminder_title')}
          message={t('full_session_lecture_reminder_message')}
          closeAriaLabel={t('full_session_lecture_reminder_close_aria')}
          onClose={() => setLectureReminderOpen(false)}
        />
      )}
      {isLectureVideoOpen && (
        <div
          className="sidebar-lecture-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sidebar-full-session-video-title"
          onClick={() => setIsLectureVideoOpen(false)}
        >
          <div
            className="sidebar-lecture-modal-panel"
            ref={lectureDrag.panelRef}
            style={lectureDrag.panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`sidebar-lecture-modal-header${lectureDrag.isDragging ? ' is-dragging' : ''}`}
              {...lectureDrag.dragHandleProps}
            >
              <h3 id="sidebar-full-session-video-title">Full Session Lecture Video</h3>
              <button
                type="button"
                className="sidebar-lecture-modal-close"
                onClick={() => setIsLectureVideoOpen(false)}
                aria-label="Close lecture video"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="sidebar-lecture-modal-video">
              <iframe
                src="https://www.youtube.com/embed/0gDKySKZ1F0"
                title="Full Session lecture video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientWheelSelector;
